import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { ReactNode } from "react";

interface PremiumCardProps {
  children: ReactNode;
  className?: string;
  hoverGradient?: "accent" | "destructive" | "primary";
  delay?: number;
  minHeight?: string;
}

export const PremiumCard = ({
  children,
  className,
  hoverGradient = "accent",
  delay = 0,
  minHeight
}: PremiumCardProps) => {
  const gradientColors = {
    accent: "from-accent/5",
    destructive: "from-destructive/5",
    primary: "from-primary/5"
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
      viewport={{ once: true }}
      className={cn(
        "group relative overflow-hidden flex flex-col",
        "backdrop-blur-xl bg-card/80",
        "border-2 border-accent/40",
        "hover:border-accent/60",
        "shadow-lg hover:shadow-xl",
        "rounded-[33px]",
        "transition-all duration-300",
        minHeight && `min-h-[${minHeight}]`,
        className
      )}
    >
      {/* Gradient-Overlay beim Hover */}
      <div 
        className={cn(
          "absolute inset-0 bg-gradient-to-b to-transparent",
          gradientColors[hoverGradient],
          "opacity-0 group-hover:opacity-100",
          "transition-all duration-500"
        )} 
      />
      
      {/* Content mit z-index */}
      <div className="relative z-10 flex flex-col h-full p-6 sm:p-8">
        {children}
      </div>
    </motion.div>
  );
};

// Helper: Icon mit Glow-Effekt
interface IconWithGlowProps {
  icon: React.ComponentType<{ className?: string }>;
  size?: "sm" | "md" | "lg";
}

export const IconWithGlow = ({ icon: Icon, size = "md" }: IconWithGlowProps) => {
  const sizes = {
    sm: "w-8 h-8",
    md: "w-10 h-10 sm:w-12 sm:h-12",
    lg: "w-12 h-12 sm:w-14 sm:h-14"
  };

  return (
    <div className="relative">
      <div className="absolute inset-0 bg-accent/20 blur-xl rounded-full" />
      <div className="relative bg-accent/10 p-4 rounded-2xl border border-accent/30">
        <Icon className={cn(sizes[size], "text-accent")} />
      </div>
    </div>
  );
};

// Helper: Status Badge
interface StatusBadgeProps {
  text: string;
  variant?: "accent" | "destructive" | "warning" | "success";
}

export const StatusBadge = ({ text, variant = "accent" }: StatusBadgeProps) => {
  const variants = {
    accent: "bg-accent/10 text-accent border-accent/30",
    destructive: "bg-destructive/10 text-destructive border-destructive/30",
    warning: "bg-orange-500/10 text-orange-500 border-orange-500/30",
    success: "bg-green-500/10 text-green-500 border-green-500/30"
  };

  return (
    <span className={cn(
      "text-xs font-medium px-3 py-1 rounded-full border",
      variants[variant]
    )}>
      {text}
    </span>
  );
};
